import type { Planet, PlanetPosition, YogaResult } from '@astrally/shared';
import { signCount, signLord, signName } from '../core/zodiac.js';

/**
 * Yoga/dosha registry. Each rule declares whether it needs the lagna;
 * lagna-dependent rules are automatically suppressed when birth time is not
 * exact. This is a foundation — add rules by appending to YOGA_RULES.
 *
 * Presence statements are structural facts ("Jupiter is in a kendra from the
 * Moon"), never predictions.
 */

export interface YogaContext {
  positions: PlanetPosition[];
  /** Sidereal lagna sign 0-11 when the birth time is exact. */
  lagnaSign?: number;
}

export interface YogaRule {
  id: string;
  name: string;
  kind: 'yoga' | 'dosha';
  requiresLagna: boolean;
  evaluate(ctx: YogaContext): { present: boolean; factors: string[] } | null;
  summary: string;
}

function pos(ctx: YogaContext, planet: Planet): PlanetPosition | undefined {
  return ctx.positions.find((p) => p.planet === planet);
}

export const YOGA_RULES: YogaRule[] = [
  {
    id: 'gajakesari',
    name: 'Gajakesari Yoga',
    kind: 'yoga',
    requiresLagna: false,
    summary: 'Jupiter in a kendra (1/4/7/10) from the Moon.',
    evaluate(ctx) {
      const moon = pos(ctx, 'moon');
      const jup = pos(ctx, 'jupiter');
      if (!moon || !jup) return null;
      const offset = signCount(moon.sidereal.sign, jup.sidereal.sign);
      const present = [1, 4, 7, 10].includes(offset);
      return {
        present,
        factors: [
          `Moon in ${signName(moon.sidereal.sign)}`,
          `Jupiter in ${signName(jup.sidereal.sign)} (${offset} from Moon)`,
        ],
      };
    },
  },
  {
    id: 'budhaditya',
    name: 'Budhaditya Yoga',
    kind: 'yoga',
    requiresLagna: false,
    summary: 'Sun and Mercury conjunct in the same sign.',
    evaluate(ctx) {
      const sun = pos(ctx, 'sun');
      const mer = pos(ctx, 'mercury');
      if (!sun || !mer) return null;
      const present = sun.sidereal.sign === mer.sidereal.sign;
      return {
        present,
        factors: [
          `Sun in ${signName(sun.sidereal.sign)}`,
          `Mercury in ${signName(mer.sidereal.sign)}`,
        ],
      };
    },
  },
  {
    id: 'chandra-mangala',
    name: 'Chandra-Mangala Yoga',
    kind: 'yoga',
    requiresLagna: false,
    summary: 'Moon and Mars conjunct in the same sign.',
    evaluate(ctx) {
      const moon = pos(ctx, 'moon');
      const mars = pos(ctx, 'mars');
      if (!moon || !mars) return null;
      const present = moon.sidereal.sign === mars.sidereal.sign;
      return {
        present,
        factors: [
          `Moon in ${signName(moon.sidereal.sign)}`,
          `Mars in ${signName(mars.sidereal.sign)}`,
        ],
      };
    },
  },
  {
    id: 'kemadruma',
    name: 'Kemadruma Yoga',
    kind: 'dosha',
    requiresLagna: false,
    summary: 'No graha (other than Sun/nodes) in the 2nd or 12th from the Moon, or with it.',
    evaluate(ctx) {
      const moon = pos(ctx, 'moon');
      if (!moon) return null;
      const others = ctx.positions.filter(
        (p) => !['moon', 'sun', 'rahu', 'ketu', 'uranus', 'neptune', 'pluto'].includes(p.planet),
      );
      const occupied = others.some((p) => {
        const offset = signCount(moon.sidereal.sign, p.sidereal.sign);
        return offset === 1 || offset === 2 || offset === 12;
      });
      return {
        present: !occupied,
        factors: [
          `Moon in ${signName(moon.sidereal.sign)}`,
          occupied
            ? 'Support present around the Moon (base rule not formed)'
            : 'No supporting graha in 12th/1st/2nd from Moon (base rule; cancellations not yet evaluated)',
        ],
      };
    },
  },
  {
    id: 'raja-kendra-trikona',
    name: 'Raja Yoga (kendra-trikona sambandha)',
    kind: 'yoga',
    requiresLagna: true,
    summary: 'A kendra lord conjunct a trikona lord (simplified conjunction rule).',
    evaluate(ctx) {
      if (ctx.lagnaSign === undefined) return null;
      const kendraSigns = [1, 4, 7, 10].map((h) => (ctx.lagnaSign! + h - 1) % 12);
      const trikonaSigns = [1, 5, 9].map((h) => (ctx.lagnaSign! + h - 1) % 12);
      const kendraLords = new Set(kendraSigns.map(signLord));
      const trikonaLords = new Set(trikonaSigns.map(signLord));
      for (const kl of kendraLords) {
        for (const tl of trikonaLords) {
          if (kl === tl) continue;
          const a = pos(ctx, kl);
          const b = pos(ctx, tl);
          if (a && b && a.sidereal.sign === b.sidereal.sign) {
            return {
              present: true,
              factors: [
                `${kl} (kendra lord) and ${tl} (trikona lord) conjunct in ${signName(a.sidereal.sign)}`,
              ],
            };
          }
        }
      }
      return { present: false, factors: ['No kendra-lord/trikona-lord conjunction'] };
    },
  },
  {
    id: 'mangal-dosha',
    name: 'Mangal Dosha',
    kind: 'dosha',
    requiresLagna: true,
    summary: 'Mars in house 1, 2, 4, 7, 8 or 12 from the lagna (classical rule; traditions vary).',
    evaluate(ctx) {
      const mars = pos(ctx, 'mars');
      if (!mars || ctx.lagnaSign === undefined) return null;
      const house = signCount(ctx.lagnaSign, mars.sidereal.sign);
      const present = [1, 2, 4, 7, 8, 12].includes(house);
      return {
        present,
        factors: [`Mars in house ${house} from lagna`],
      };
    },
  },
  {
    id: 'mangal-dosha-moon',
    name: 'Mangal Dosha (from Moon)',
    kind: 'dosha',
    requiresLagna: false,
    summary: 'Mars in house 1, 2, 4, 7, 8 or 12 counted from the Moon.',
    evaluate(ctx) {
      const mars = pos(ctx, 'mars');
      const moon = pos(ctx, 'moon');
      if (!mars || !moon) return null;
      const house = signCount(moon.sidereal.sign, mars.sidereal.sign);
      const present = [1, 2, 4, 7, 8, 12].includes(house);
      return { present, factors: [`Mars in house ${house} from Moon`] };
    },
  },
  {
    id: 'kaal-sarpa',
    name: 'Kaal Sarpa (axis pattern)',
    kind: 'dosha',
    requiresLagna: false,
    summary: 'All seven grahas on one side of the Rahu–Ketu axis.',
    evaluate(ctx) {
      const rahu = pos(ctx, 'rahu');
      if (!rahu) return null;
      const grahas = ctx.positions.filter((p) =>
        ['sun', 'moon', 'mars', 'mercury', 'jupiter', 'venus', 'saturn'].includes(p.planet),
      );
      if (grahas.length < 7) return null;
      const inFirstHalf = (lon: number) => {
        const rel = (lon - rahu.lonSidereal + 360) % 360;
        return rel < 180;
      };
      const allFirst = grahas.every((p) => inFirstHalf(p.lonSidereal));
      const allSecond = grahas.every((p) => !inFirstHalf(p.lonSidereal));
      return {
        present: allFirst || allSecond,
        factors: [
          allFirst || allSecond
            ? 'All grahas on one side of the nodal axis'
            : 'Grahas on both sides of the nodal axis',
        ],
      };
    },
  },
];

export function evaluateYogas(ctx: YogaContext): YogaResult[] {
  const results: YogaResult[] = [];
  for (const rule of YOGA_RULES) {
    if (rule.requiresLagna && ctx.lagnaSign === undefined) continue; // suppressed, not guessed
    const r = rule.evaluate(ctx);
    if (!r) continue;
    results.push({
      id: rule.id,
      name: rule.name,
      kind: rule.kind,
      present: r.present,
      requiresLagna: rule.requiresLagna,
      summary: rule.summary,
      factors: r.factors,
    });
  }
  return results;
}
